// src/pages/tournament/TeamsPanel.jsx
// The fixed teams of a 'fixed-teams' tournament: pair up active players once, then round planning pairs
// the teams against each other (no partner search: see FormatSetting). Locked once the first round exists.
import React, { useState } from 'react';
import { useConfirm } from '../../components/ConfirmDialog';
import { PlusIcon, TrashIcon } from '../../components/icons';
import { groupName } from '../../utils/groups';

const teamName = (team) => team.players.map((player) => `${player.firstName} ${player.lastName}`).join(' & ');

function PlayerSelect({ label, value, onChange, options }) {
    return (
        <select aria-label={label} value={value ?? ''} onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}>
            <option value="">Choose…</option>
            {options.map((player) => (
                <option key={player.playerId} value={player.playerId}>
                    {player.firstName} {player.lastName}
                </option>
            ))}
        </select>
    );
}

function TeamsPanel({ players, fixedTeams, onAdd, onDelete, locked, grouped = false, groupCount = 1, onMove, onRegroup }) {
    const confirm = useConfirm();
    const [first, setFirst] = useState(null);
    const [second, setSecond] = useState(null);
    const [busy, setBusy] = useState(false);
    const teamedIds = new Set(fixedTeams.flatMap((team) => team.players.map((player) => player.playerId)));
    const available = players.filter((player) => player.active !== false && !teamedIds.has(player.playerId));
    const optionsFor = (own) => available.filter((player) => (player.playerId !== first && player.playerId !== second) || player.playerId === own);

    const makeTeam = async () => {
        setBusy(true);
        const team = await onAdd([first, second]);
        setBusy(false);
        if (team) {
            setFirst(null);
            setSecond(null);
        }
    };

    const removeTeam = async (team) => {
        const name = teamName(team);
        const confirmed = await confirm({
            title: `Remove the team ${name}?`,
            message: 'Both players stay in the tournament; they can be put on another team.',
            confirmLabel: 'Remove team',
            danger: false,
        });
        if (confirmed) onDelete(team.teamId);
    };

    const renderTeam = (team) => (
        <li key={team.teamId}>
            <span>{teamName(team)}</span>
            {grouped && !locked && (
                <select
                    className="group-select"
                    aria-label={`Group of ${teamName(team)}`}
                    value={team.group ?? 0}
                    onChange={(event) => onMove(team.teamId, Number(event.target.value))}
                >
                    {Array.from({ length: groupCount }, (_, group) => (
                        <option key={group} value={group}>
                            Group {groupName(group)}
                        </option>
                    ))}
                </select>
            )}
            {!locked && (
                <button
                    className="btn btn-ghost btn-icon btn-sm btn-icon-danger"
                    aria-label={`Remove team ${teamName(team)}`}
                    onClick={() => removeTeam(team)}
                >
                    <TrashIcon size={16} />
                </button>
            )}
        </li>
    );

    return (
        <section className="card teams-card" aria-label="Teams">
            <h2>Teams</h2>
            {locked ? (
                <p className="hint">
                    Teams are locked once the first round has been planned: pairs already play together for the rest of
                    the tournament.
                </p>
            ) : (
                <>
                    <p className="hint">
                        {grouped
                            ? 'Pair up players into fixed teams. Teams are dealt over the groups at random; move them if you like. Each group plays a round-robin, then the best teams go through to the knockout.'
                            : 'Pair up players into fixed teams. Rounds then pair whole teams against each other.'}
                    </p>
                    <div className="team-picks">
                        <PlayerSelect label="First player" value={first} onChange={setFirst} options={optionsFor(first)} />
                        <PlayerSelect label="Second player" value={second} onChange={setSecond} options={optionsFor(second)} />
                        <button className="btn btn-sm btn-primary" type="button" onClick={makeTeam} disabled={first === null || second === null || busy}>
                            <PlusIcon size={14} /> Make team
                        </button>
                    </div>
                    {available.length === 0 && fixedTeams.length === 0 && (
                        <p className="hint">No active players yet: add players first.</p>
                    )}
                </>
            )}
            {grouped && !locked && fixedTeams.length > 1 && (
                <button className="btn btn-sm" type="button" onClick={onRegroup}>
                    Shuffle groups
                </button>
            )}
            {fixedTeams.length === 0 ? (
                <p className="hint">No teams yet.</p>
            ) : grouped ? (
                Array.from({ length: groupCount }, (_, group) => {
                    const members = fixedTeams.filter((team) => (team.group ?? 0) === group);
                    return (
                        <div key={group} className="team-group">
                            <h3>Group {groupName(group)}</h3>
                            {members.length === 0 ? <p className="hint">No teams in this group.</p> : <ul className="team-list">{members.map(renderTeam)}</ul>}
                        </div>
                    );
                })
            ) : (
                <ul className="team-list">{fixedTeams.map(renderTeam)}</ul>
            )}
        </section>
    );
}

export default TeamsPanel;
